/**
 * datevBelegeAccess — fail-closed Sichtbarkeits-Logik für das DATEV-Belege-Paket
 * (ZIP-Download, M7b-3).
 *
 * Single source of truth dafür, OB der „Belege-Paket herunterladen (ZIP)"-Button in
 * `DatevExportDialog` sichtbar ist. Muster 1:1 `messagingAccess.ts` (gleiche zwei
 * Freischalt-Pfade, gleiche Beta-Tenant-UID, gated auf `effectiveScopeUid`):
 *
 *   1. Beta-Tenant-Allowlist: der Test-Tenant sieht das Belege-Paket LIVE, unabhängig
 *      vom Env-Flag (kontrollierte Erprobung).
 *   2. Allgemeine Freigabe: `REACT_APP_DATEV_BELEGE_ENABLED==='true'`.
 *
 * Fail-closed: ohne Allowlist-Treffer UND ohne Flag ist der Rückgabewert `false` —
 * der Belege-Button bleibt unsichtbar, der bestehende CSV-Export (M7) ist davon
 * vollkommen unberührt (eigener, ungegateter Pfad in `DatevExportDialog`).
 *
 * Tier-Term (Pricing-Repackaging PR-T4, architecture-pricing-repackaging.md §3.2/§3.2b):
 * die DATEV-Kette ist ab GA ein **Ultra**-Differenzierer — ABER ausschließlich im
 * **Owner-Kontext**. Im **Advisor-Kontext** (Steuerberater im Mandanten-Tenant)
 * bleibt das Gate rein Loop-B-/Kanzlei-gegatet OHNE Owner-Tier-Term: ein StB darf
 * NIE durch den fehlenden Ultra-Status seines Mandanten ausgesperrt werden (§3.2b).
 * Deshalb der explizite `isAdvisorContext`-Parameter. Inert, solange das Flag live
 * `false`/unset ist ⇒ nur die Beta-Allowlist greift, byte-identisch zum IST.
 *
 * Go-Live (M7b-4-[E-RECHNUNG-GATE] muss grün sein, s. modules-m7b.md):
 * `REACT_APP_DATEV_BELEGE_ENABLED` bleibt bis dahin auf `false`/unset — dieses Modul
 * ist inert, solange weder Flag noch Allowlist greifen.
 *
 * @module datevBelegeAccess
 */

import type { Tier } from './entitlements';

/**
 * Beta-Erprobung DATEV-Belege-Paket — entfernen/erweitern, wenn allgemein freigegeben.
 *
 * Nur dieser Tenant-Scope (`effectiveScopeUid`) erhält Zugriff unabhängig vom
 * Env-Flag. `b3DRR1Cj63cRjdJg5VCEUpBL7hk1` = Test-Account daniel_eich@outlook.com
 * (identisch zu `MESSAGING_BETA_TENANT_UIDS`/`RECEIPT_SCAN_BETA_UIDS`).
 * HARTKODIERT für die kontrollierte Live-Erprobung; vor allgemeiner Freigabe
 * (Flag = 'true' für alle) wieder leeren.
 */
export const DATEV_BELEGE_BETA_TENANT_UIDS: readonly string[] = ['b3DRR1Cj63cRjdJg5VCEUpBL7hk1'];

/**
 * Allgemeines Feature-Flag für das DATEV-Belege-Paket. Fail-closed: nur bei
 * explizitem `'true'` aktiv. Modul-Konstante, einmalig beim Bundle-Load ausgewertet.
 * Bleibt der spätere Allgemein-Freigabe-Pfad — bis dahin treibt die Beta-Allowlist
 * die Sichtbarkeit (siehe {@link canUseDatevBelege}). Muss im Gleichschritt mit dem
 * Function-seitigen `DATEV_BELEGE_ENABLED`-Flag gesetzt werden (M7b-2), sonst zeigt
 * die UI einen Button, dessen Aufruf die Function mit `failed-precondition` ablehnt.
 */
export const DATEV_BELEGE_FLAG: boolean = process.env.REACT_APP_DATEV_BELEGE_ENABLED === 'true';

/**
 * Entscheidet fail-closed, ob das DATEV-Belege-Paket (ZIP) für einen Tenant-Scope
 * sichtbar ist.
 *
 * WICHTIG: gated auf `effectiveScopeUid` (den Tenant-SCOPE), NICHT auf
 * `currentUser.uid` — analog `canUseMessaging`. Rein kosmetisches Client-Gating;
 * die harte Sperre bleibt die Function-seitige Auth-/Autorisierungs-Prüfung
 * (`buildDatevBelegePaket`, Owner ODER aktives `tenant_members`-Member).
 *
 * @param scopeUid - `effectiveScopeUid` aus `useAuth()` (oder null/undefined,
 *                   solange noch nicht aufgelöst).
 * @param tier - effektiver Tier des Owners (`resolveTier(sellerData)` aus
 *               `entitlements.ts`). Nur im Owner-Kontext ausgewertet.
 * @param isAdvisorContext - `true`, wenn der aktuelle Nutzer ein Steuerberater im
 *               Mandanten-Kontext ist (§3.2b). Dann KEIN Owner-Tier-Term — reines
 *               Loop-B-/Flag-Gate (Advisor wird nie durch Mandanten-Tier gesperrt).
 * @returns `true`, wenn `scopeUid` auf der Beta-Tenant-Allowlist steht ODER
 *          (im Advisor-Kontext) das Flag aktiv ist ODER (im Owner-Kontext) das
 *          Flag aktiv ist UND der Owner den Ultra-Tier hat. Sonst `false`.
 */
export function canUseDatevBelege(
  scopeUid: string | null | undefined,
  tier: Tier,
  isAdvisorContext: boolean,
): boolean {
  if (!!scopeUid && DATEV_BELEGE_BETA_TENANT_UIDS.includes(scopeUid)) {
    return true;
  }
  // §3.2b Advisor-Kontext: reines Loop-B-/Kanzlei-Gate, KEIN Owner-Tier-Term.
  if (isAdvisorContext) {
    return DATEV_BELEGE_FLAG;
  }
  // Owner-Kontext: allgemeine Freigabe erst ab Ultra.
  return DATEV_BELEGE_FLAG && tier === 'ultra';
}
